/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, JSX, VNode } from 'preact';
import {
  Modal,
  Button,
  ImageSwatch,
  Price,
  ContentGrid,
  ImageNodeRenderProps,
} from '@adobe-commerce/elsie/components';
import { useState, useEffect } from 'preact/hooks';
import { GiftWrappingConfigProps, GiftOptionsViewProps } from '@/cart/types';
import { useText } from '@adobe-commerce/elsie/i18n';

interface GiftOptionModalProps {
  giftWrappingConfig: GiftWrappingConfigProps[];
  showModal: boolean;
  productName: string;
  view: GiftOptionsViewProps;
  imageSwatchImageNode?: VNode | ((props: ImageNodeRenderProps) => JSX.Element);
  setShowModal: () => void;
  updateGiftOptions: (
    name: string,
    value?: string | boolean | number,
    extraGiftOptions?: Record<string, string | boolean | number>
  ) => void;
}

export const GiftOptionModal: FunctionComponent<GiftOptionModalProps> = ({
  view,
  showModal,
  productName,
  giftWrappingConfig,
  imageSwatchImageNode,
  setShowModal,
  updateGiftOptions,
}) => {
  const dictionary = useText({
    modalTitle: 'Cart.GiftOptions.modal.title',
    defaultTitle: 'Cart.GiftOptions.modal.defaultTitle',
    modalWrappingText: 'Cart.GiftOptions.modal.wrappingText',
    modalWrappingSubText: 'Cart.GiftOptions.modal.wrappingSubText',
    modalConfirmButton: 'Cart.GiftOptions.modal.modalConfirmButton',
    modalCancelButton: 'Cart.GiftOptions.modal.modalCancelButton',
    ariaLabelWrapping: 'Cart.GiftOptions.modal.ariaLabelWrapping',
  });

  const [giftWrappingItem, setGiftWrappingItem] =
    useState<GiftWrappingConfigProps>();

  useEffect(() => {
    const selectedGiftWrapping =
      giftWrappingConfig.find((item) => item.selected) ?? giftWrappingConfig[0];

    setGiftWrappingItem(selectedGiftWrapping);
  }, [giftWrappingConfig]);

  if (!showModal || !giftWrappingConfig.length) return null;

  const renderTitle = productName
    ? `${dictionary.modalTitle} ${productName}`
    : dictionary.defaultTitle;

  return (
    <Modal
      data-testid={`gift-option-modal-${view}`}
      className={'cart-gift-options-view__modal'}
      size={'medium'}
      title={
        <>
          <h2>{renderTitle}</h2>
          {giftWrappingItem && giftWrappingItem?.price?.value > 0 ? (
            <Price
              amount={giftWrappingItem.price.value}
              currency={giftWrappingItem.price.currency}
              weight="normal"
            />
          ) : null}
        </>
      }
      centered={true}
      onClose={setShowModal}
    >
      <div className="cart-gift-options-view__modal-content">
        <span className="cart-gift-options-view__modal-text">
          {dictionary.modalWrappingText}
        </span>
        <ContentGrid
          emptyGridContent={<></>}
          maxColumns={6}
          columnWidth="100px"
          className="cart-gift-options-view__modal-grid"
        >
          {giftWrappingConfig.map((wrapItem) => (
            <ImageSwatch
              groupAriaLabel={dictionary.ariaLabelWrapping}
              selected={giftWrappingItem?.uid === wrapItem?.uid}
              onValue={() => {
                setGiftWrappingItem(wrapItem);
              }}
              imageNode={imageSwatchImageNode}
              name={'giftWrappingId'}
              value={wrapItem.uid}
              src={wrapItem.image.url}
              alt={wrapItem.design}
              label={wrapItem.design}
              key={wrapItem.uid}
              data-testid={`gift-option-modal-image-${wrapItem.uid}`}
              className={'cart-gift-options-view__modal-grid-item'}
            />
          ))}
        </ContentGrid>
        <span className="cart-gift-options-view__modal-sub-text">
          {giftWrappingItem?.design}
        </span>
      </div>

      <Button
        data-testid="gift-option-modal-confirm-button"
        type="button"
        onClick={() => {
          updateGiftOptions('giftWrappingId', giftWrappingItem?.uid, {
            isGiftWrappingSelected: true,
          });
          setShowModal();
        }}
      >
        {dictionary.modalConfirmButton}
      </Button>
      <Button
        type="button"
        variant="secondary"
        onClick={setShowModal}
        data-testid="gift-option-modal-cancel-button"
      >
        {dictionary.modalCancelButton}
      </Button>
    </Modal>
  );
};
